import React, { useMemo } from "react";

const SIGNAL_ITEMS = [
  { label: "Control", colorVar: "control", shape: "circle", match: (types) => types.has("control") },
  { label: "Mic", colorVar: "mic", shape: "circle", match: (_types, levels) => levels.has("mic") || levels.has("mic_line") },
  { label: "Instrument", colorVar: "instrument", shape: "circle", match: (_types, levels) => levels.has("instrument") },
  { label: "Mic/Line", colorVar: null, shape: "split-mic-line", match: (_types, levels) => levels.has("mic_line") },
  { label: "Line", colorVar: "line", shape: "circle", match: (_types, levels) => levels.has("line") || levels.has("mic_line") },
  { label: "MIDI", colorVar: "midi", shape: "circle", match: (types) => types.has("midi") },
  { label: "Speaker", colorVar: "speaker", shape: "circle", match: (_types, levels) => levels.has("speaker") },
];

const CONNECTOR_ITEMS = [
  { label: '1/4"', shape: "circle", match: (conns) => conns.has("quarter_inch_ts") || conns.has("quarter_inch_trs") },
  { label: '1/8"', shape: "circle-sm", match: (conns) => conns.has("eighth_inch_ts") || conns.has("eighth_inch_trs") || conns.has("eighth_inch_trrs") },
  { label: "XLR", shape: "rounded-rect", match: (conns) => conns.has("xlr") },
  { label: "XLR Combo", shape: "xlr-combo", match: (conns) => conns.has("xlr_combo") },
  { label: "Mini-XLR (TA3F / TA4F)", shape: "mini-rounded-rect", match: (conns) => conns.has("mini_xlr_3pin") || conns.has("mini_xlr_4pin") },
  { label: "Speakon", shape: "hexagon", match: (conns) => conns.has("speakon") },
  { label: "MIDI DIN", shape: "diamond", match: (conns) => conns.has("midi_din") },
  { label: "USB", shape: "rounded-square", match: (conns) => conns.has("usb_a") || conns.has("usb_b") || conns.has("usb_c") },
  { label: "Other", shape: "other", match: (conns) => conns.has("proprietary") },
  { label: "Wireless", shape: "wireless", noFill: true, match: (conns) => conns.has("wireless") },
];

const POWER_ITEMS = [
  { label: "2.1mm Barrel", shape: "power-barrel-sm", match: (conns) => conns.has("barrel_2_1mm") },
  { label: "2.5mm Barrel", shape: "power-barrel-lg", match: (conns) => conns.has("barrel_2_5mm") },
  { label: "RCA", shape: "power-rca", match: (conns) => conns.has("rca") },
  { label: "AC", shape: "power-ac", match: (conns) => conns.has("ac_cable") },
  { label: "USB", shape: "power-usb", match: (conns) => conns.has("usb_a") || conns.has("usb_c") },
  { label: "Other", shape: "power-other", match: (conns) => conns.has("proprietary") },
];

function collectPortData(nodes) {
  const signalTypes = new Set();
  const signalLevels = new Set();
  const connectors = new Set();
  const powerConnectors = new Set();

  for (const node of nodes) {
    if (node.type === "signalBoundary") {
      const b = node.data?.boundary;
      if (b) {
        if (b.signal_type) signalTypes.add(b.signal_type);
        if (b.signal_level) signalLevels.add(b.signal_level);
        if (b.connector) connectors.add(b.connector);
      }
      continue;
    }

    const device = node.data?.device;
    if (!device) continue;

    const ports = device.device_ports || [];
    for (const port of ports) {
      if (port.signal_type) signalTypes.add(port.signal_type);
      if (port.signal_level) signalLevels.add(port.signal_level);
      if (port.connector) connectors.add(port.connector);
    }

    if (device.power_connector && device.power_connector !== "none") {
      powerConnectors.add(device.power_connector);
    }
    for (const po of device.power_outputs || []) {
      if (po.connector) powerConnectors.add(po.connector);
    }
  }

  return { signalTypes, signalLevels, connectors, powerConnectors };
}

function LegendSubsection({ title, items, swatchColor }) {
  return (
    <div className="signal-legend-subsection">
      <div className="signal-legend-subsection-title">{title}</div>
      <ul className="signal-legend-list">
        {items.map((item) => (
          <li key={item.label} className="signal-legend-item">
            <span
              className={`signal-legend-swatch signal-legend-swatch--${item.shape}`}
              style={item.noFill ? undefined : { background: item.colorVar ? `var(--color-signal-${item.colorVar})` : swatchColor }}
            />
            <span className="signal-legend-label">{item.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SignalLegend({ nodes = [] }) {
  const { signalItems, connectorItems, powerItems } = useMemo(() => {
    const { signalTypes, signalLevels, connectors, powerConnectors } = collectPortData(nodes);

    const signals = SIGNAL_ITEMS.filter((item) => item.match(signalTypes, signalLevels));

    const conns = CONNECTOR_ITEMS.filter((item) => item.match(connectors));

    const power = POWER_ITEMS.filter((item) => item.match(powerConnectors));

    return { signalItems: signals, connectorItems: conns, powerItems: power };
  }, [nodes]);

  return (
    <div className="signal-legend">
      <div className="signal-legend-sections">
        <div className="signal-legend-section">
          <div className="signal-legend-section-title">Signal</div>
          <div className="signal-legend-subsections">
            <LegendSubsection title="Connector" items={connectorItems} swatchColor="var(--color-base-muted)" />
            <LegendSubsection title="Level" items={signalItems} />
          </div>
        </div>
        {powerItems.length > 0 && (
          <div className="signal-legend-section">
            <div className="signal-legend-section-title">Power</div>
            <div className="signal-legend-subsections">
              <LegendSubsection title="Connector" items={powerItems} swatchColor="var(--color-base-muted)" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
